import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDigcompAreas, useDigcompCompetences, useDigcompLevels, DigcompArea, DigcompCompetence, DigcompLevel } from '@/hooks/useDigcompData';
import { DigcompPentagonIcon, DIGCOMP_AREA_COLORS } from './DigcompPentagonIcon';

interface DigcompCompetenceSelectorProps {
  brandId: string | null;
  competenceId: string | null;
  areaId: string | null;
  levelId?: string | null;
  onCompetenceChange: (competenceId: string | null, areaId: string | null) => void;
  onLevelChange?: (levelId: string | null) => void;
  showLevelSelector?: boolean;
  disabled?: boolean;
}

/**
 * Reusable DigComp 3.0 competence selector component.
 * Shows competences grouped by area with color-coded pentagon icons.
 * Optionally shows a level selector.
 */
export function DigcompCompetenceSelector({
  brandId,
  competenceId,
  areaId,
  levelId,
  onCompetenceChange,
  onLevelChange,
  showLevelSelector = false,
  disabled = false,
}: DigcompCompetenceSelectorProps) {
  const { data: areas = [], isLoading: areasLoading } = useDigcompAreas(brandId);
  const { data: competences = [], isLoading: competencesLoading } = useDigcompCompetences(brandId);
  const { data: levels = [], isLoading: levelsLoading } = useDigcompLevels(brandId);

  const isLoading = areasLoading || competencesLoading || (showLevelSelector && levelsLoading);

  // Group competences by area
  const competencesByArea = useMemo(() => {
    const grouped: Record<string, { area: DigcompArea; competences: DigcompCompetence[] }> = {};
    
    for (const area of areas) {
      grouped[area.id] = { area, competences: [] };
    }
    
    for (const comp of competences) {
      if (grouped[comp.area_id]) {
        grouped[comp.area_id].competences.push(comp);
      }
    }
    
    // Sort competences within each area
    for (const group of Object.values(grouped)) {
      group.competences.sort((a, b) => a.sort_order - b.sort_order);
    }
    
    return Object.values(grouped).sort((a, b) => a.area.sort_order - b.area.sort_order);
  }, [areas, competences]);

  const handleCompetenceChange = (value: string) => {
    if (value === 'none') {
      onCompetenceChange(null, null);
      return;
    }
    
    const selectedComp = competences.find(c => c.id === value);
    if (selectedComp) {
      onCompetenceChange(selectedComp.id, selectedComp.area_id);
    }
  };

  const handleLevelChange = (value: string) => {
    if (!onLevelChange) return;
    onLevelChange(value === 'none' ? null : value);
  };

  // Get area number from code (e.g., "1" from "1" or from area sort_order)
  const getAreaNumber = (area: DigcompArea): number => {
    const codeNum = parseInt(area.code, 10);
    if (!isNaN(codeNum) && codeNum >= 1 && codeNum <= 5) return codeNum;
    return area.sort_order;
  };

  // Get selected competence display
  const selectedCompetence = competences.find(c => c.id === competenceId);
  const selectedArea = areas.find(a => a.id === (selectedCompetence?.area_id || areaId));

  if (!brandId) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Competence Selector */}
      <div className="space-y-2">
        <Label>DigComp Competence</Label>
        <Select
          value={competenceId || 'none'}
          onValueChange={handleCompetenceChange}
          disabled={disabled || isLoading}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={isLoading ? 'Loading...' : 'Select a competence'}>
              {selectedCompetence ? (
                <div className="flex items-center gap-2">
                  {selectedArea && (
                    <DigcompPentagonIcon 
                      areaNumber={getAreaNumber(selectedArea)} 
                      size={16} 
                    />
                  )}
                  <span className="truncate">
                    {selectedCompetence.code}: {selectedCompetence.name}
                  </span>
                </div>
              ) : (
                <span className="text-muted-foreground">
                  {isLoading ? 'Loading...' : 'None'}
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="none">
              <span className="text-muted-foreground">None</span>
            </SelectItem>
            
            {competencesByArea.map(({ area, competences: areaCompetences }) => {
              if (areaCompetences.length === 0) return null;
              
              const areaNum = getAreaNumber(area);
              const areaColor = DIGCOMP_AREA_COLORS[areaNum as keyof typeof DIGCOMP_AREA_COLORS] || '#888';
              
              return (
                <div key={area.id}>
                  {/* Area Header */}
                  <div 
                    className="px-2 py-1.5 text-xs font-semibold text-muted-foreground flex items-center gap-2 bg-muted/50 sticky top-0"
                    style={{ borderLeft: `3px solid ${areaColor}` }}
                  >
                    <DigcompPentagonIcon areaNumber={areaNum} size={14} />
                    <span>Area {area.code}: {area.name}</span>
                  </div>
                  
                  {/* Competences in this area */}
                  {areaCompetences.map((comp) => (
                    <SelectItem key={comp.id} value={comp.id} className="pl-6">
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-2 h-2 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: areaColor }}
                        />
                        <span className="font-medium text-xs">{comp.code}</span>
                        <span className="truncate">{comp.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </div>
              );
            })}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Link this content to a specific digital competence from the DigComp 3.0 framework
        </p>
      </div>

      {/* Level Selector (Optional) */}
      {showLevelSelector && onLevelChange && (
        <div className="space-y-2">
          <Label>Proficiency Level</Label>
          <Select
            value={levelId || 'none'}
            onValueChange={handleLevelChange}
            disabled={disabled || levelsLoading}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={levelsLoading ? 'Loading...' : 'Select a level'}>
                {levels.find(l => l.id === levelId)?.name || 'None'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                <span className="text-muted-foreground">None</span>
              </SelectItem>
              {levels.map((level) => (
                <SelectItem key={level.id} value={level.id}>
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: level.badge_color || '#888' }}
                    />
                    <span className="font-medium">{level.name}</span>
                    <span className="text-muted-foreground text-xs">
                      ({level.min_score}-{level.max_score}%)
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Optional: Specify the target proficiency level for this content
          </p>
        </div>
      )}
    </div>
  );
}
